import {FOLLOWERS_COLLECTION, followerDocId} from "@buildwithflux/following-schema";
import {STARRED_COLLECTION, starredDocId} from "@buildwithflux/starring-schema";
import {useEffect, useState} from "react";

import {getFirebaseClient} from "../firebase/firebaseClient";

interface RelationshipState {
    /** `undefined` until the subscription resolves (button stays disabled). */
    readonly active: boolean | undefined;
    /** Optimistically override the rendered state before the write lands. */
    readonly setOptimistic: (next: boolean) => void;
}

/**
 * Subscribe to the existence of a single per-viewer Firestore doc (realtime,
 * no HTTP). Returns `undefined` while loading, then `true`/`false`.
 *
 * SSR-safe: Firebase is only touched inside the effect, so the server renders
 * the loading state and the client resolves it after hydration.
 */
function useDocExists(collection: string | null, docId: string | null): RelationshipState {
    const [active, setActive] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (!collection || !docId) {
            setActive(undefined);
            return;
        }

        let cancelled = false;
        let unsubscribe: (() => void) | undefined;

        void (async () => {
            const {db, firestoreModule} = await getFirebaseClient();
            if (cancelled) return;
            const ref = firestoreModule.doc(db, collection, docId);
            unsubscribe = firestoreModule.onSnapshot(
                ref,
                (snap) => {
                    if (!cancelled) setActive(snap.exists());
                },
                () => {
                    if (!cancelled) setActive(false);
                },
            );
        })();

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [collection, docId]);

    return {active, setOptimistic: setActive};
}

/** Realtime "does the viewer follow this account" state. */
export function useFollowState(accountUid: string, viewerUid: string | null): RelationshipState {
    return useDocExists(
        viewerUid ? FOLLOWERS_COLLECTION : null,
        viewerUid ? followerDocId(accountUid, viewerUid) : null,
    );
}

/** Realtime "has the viewer starred this document" state. */
export function useStarState(documentUid: string, viewerUid: string | null): RelationshipState {
    return useDocExists(
        viewerUid ? STARRED_COLLECTION : null,
        viewerUid ? starredDocId(documentUid, viewerUid) : null,
    );
}
